"use client";

import { Label } from "@/components/ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { usePatientCreditReasons } from "@/hooks/use-patient-credit-reasons";

interface ReasonOption {
  id: string;
  name: string;
}

/**
 * Reason state for a manual patient credit. `current` is the credit's saved
 * reason when editing, kept as an option even after it's archived.
 */
export function useCreditReasonField(current?: ReasonOption | null) {
  const { patientCreditReasons, isPending, isError, refetch } =
    usePatientCreditReasons();
  // The saved reason keeps its issued name (the server won't re-snapshot an unchanged id).
  const options = !current
    ? patientCreditReasons
    : patientCreditReasons.some((r) => r.id === current.id)
      ? patientCreditReasons.map((r) => (r.id === current.id ? current : r))
      : [...patientCreditReasons, current];
  return {
    options,
    /** Once the clinic has any active reason, a new or changed reason is required. */
    hasConfiguredReasons: patientCreditReasons.length > 0,
    /** Until the list loads we can't tell whether a reason is required. */
    isUnresolved: isPending || isError,
    isError,
    retry: () => void refetch(),
    /** The picked id if it's still offered; a reason archived elsewhere drops out instead of being resubmitted. */
    offered: (id: string) => (options.some((r) => r.id === id) ? id : ""),
  };
}

interface CreditReasonFieldProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  error?: string | null;
  field: ReturnType<typeof useCreditReasonField>;
}

export function CreditReasonField({
  id = "credit-reason",
  value,
  onChange,
  error,
  field,
}: CreditReasonFieldProps) {
  if (field.isError) {
    return (
      <button
        type="button"
        className="text-caption text-primary hover:underline"
        onClick={field.retry}
      >
        Couldn&apos;t load credit reasons. Try again.
      </button>
    );
  }
  if (field.options.length === 0) return null;
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>
        {field.hasConfiguredReasons ? "Reason" : "Reason (optional)"}
      </Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id={id} hasError={!!error}>
          <SelectValue placeholder="Select a reason" />
        </SelectTrigger>
        <SelectContent>
          {field.options.map((reason) => (
            <SelectItem key={reason.id} value={reason.id}>
              {reason.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {error && <p className="text-caption text-status-error-text">{error}</p>}
    </div>
  );
}
